import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { Provider, useDispatch, useSelector } from 'react-redux';
import './App.css';
import brandingConfig from './config/brandingConfig';

// Store
import { store } from './redux/store';
import { checkAuthState } from './redux/slices/authSlice';

// Theme
import theme from './styles/theme';

// Components
import Navbar from './components/common/Navbar';
import LandingPage from './components/auth/LandingPage';
import Dashboard from './components/dashboard/Dashboard';
import Loading from './components/common/Loading';
import CookieBanner from './components/common/CookieBanner';
import PrivacyPolicy from './components/auth/PrivacyPolicy';
import Imprint from './components/auth/Imprint';

// Providers
import AppProvider from './providers/AppProvider';

// Fahrer-Komponenten
import DriversList from './components/drivers/DriversList';
import DriverDetails from './components/drivers/DriverDetails';
import DriverForm from './components/drivers/DriverForm';

// Kunden-Komponenten
import CustomersList from './components/customers/CustomersList';
import CustomerDetails from './components/customers/CustomerDetails';
import CustomerForm from './components/customers/CustomerForm';

// Fahrzeug-Komponenten
import VehiclesList from './components/vehicles/VehiclesList';
import VehicleDetails from './components/vehicles/VehicleDetails';
import VehicleForm from './components/vehicles/VehicleForm';

// Fahrt-Komponenten
import RidesList from './components/rides/RidesList';
import RideDetails from './components/rides/RideDetails';
import RideForm from './components/rides/RideForm';
import RecurringOrdersList from './components/rides/RecurringOrdersList';
import RecurringOrderEditForm from './components/rides/RecurringOrderEditForm';

// Schicht-Komponenten
import ShiftsList from './components/shifts/ShiftsList';
import ShiftDetails from './components/shifts/ShiftDetails';
import ShiftForm from './components/shifts/ShiftForm';
import ShiftCalendar from './components/shifts/ShiftCalendar';
import ShiftPlanner from './components/shifts/ShiftPlanner';
import PlannedShiftForm from './components/shifts/PlannedShiftForm';

// Berichts-Komponente
import ReportsPage from './components/reports/ReportsPage';

// Einstellungen-Komponente
import SettingsPage from './components/settings/SettingsPage';

// Öffentliche Seiten
import MobileAppRedirect from './components/public/MobileAppRedirect';
import MobilePrivacyPolicy from './components/mobile/MobilePrivacyPolicy';
import MobileImprint from './components/mobile/MobileImprint';
import AccountDeletion from './components/mobile/AccountDeletion';

// Vorlagen-Komponenten
import TemplatesList from './components/templates/TemplatesList';
import TemplateForm from './components/templates/TemplateForm';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useSelector(state => state.auth);
  
  if (isLoading) {
    return <Loading message="Authentifizierung wird überprüft..." />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Auth Check Wrapper
const AuthCheckWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const { isLoading } = useSelector(state => state.auth);
  
  useEffect(() => {
    dispatch(checkAuthState());
  }, [dispatch]);
  
  if (isLoading) {
    return <Loading message="Anwendung wird geladen..." />;
  }
  
  return children;
};

function AppContent() {
  const { isAuthenticated } = useSelector(state => state.auth);
  
  // Setze Dokumententitel und Favicon basierend auf Branding-Konfiguration
  useEffect(() => {
    // Setze Dokumententitel
    document.title = `${brandingConfig.appName} - Admin Portal`;
    
    // Setze Theme-Color Meta-Tag auf die Primärfarbe
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', brandingConfig.colors.primary);
    }
  }, []);
  
  // Prüfe, ob es sich um die Default-App handelt (NextCab) oder um eine White-Label-App
  const isDefaultApp = brandingConfig.appName === "NextCab";

  return (
    <Router>
      <AuthCheckWrapper>
        {isAuthenticated && <Navbar />}
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to="/dashboard" />
                : <LandingPage directLogin={!isDefaultApp} />
            }
          />

          {/* Rechtliche Seiten - öffentlich zugänglich */}
          <Route 
            path="/datenschutz" 
            element={<PrivacyPolicy />} 
          />
          <Route
            path="/impressum"
            element={<Imprint />}
          />
          <Route
            path="/mobile-app"
            element={<MobileAppRedirect />}
          />
          <Route
            path="/mobile/datenschutz"
            element={<MobilePrivacyPolicy />}
          />
          <Route
            path="/mobile/impressum"
            element={<MobileImprint />}
          />
          <Route
            path="/mobile/konto-loeschen"
            element={<AccountDeletion />}
          />

          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer" 
            element={
              <ProtectedRoute>
                <DriversList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/:id" 
            element={
              <ProtectedRoute>
                <DriverDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <DriverForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrer/neu" 
            element={
              <ProtectedRoute>
                <DriverForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden" 
            element={
              <ProtectedRoute>
                <CustomersList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden/:id" 
            element={
              <ProtectedRoute>
                <CustomerDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/kunden/neu" 
            element={
              <ProtectedRoute>
                <CustomerForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge" 
            element={
              <ProtectedRoute>
                <VehiclesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/:id" 
            element={
              <ProtectedRoute>
                <VehicleDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrzeuge/neu" 
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten" 
            element={
              <ProtectedRoute>
                <RidesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten/:id" 
            element={
              <ProtectedRoute>
                <RideDetails />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/fahrten/:id/bearbeiten"
            element={
              <ProtectedRoute>
                <RideForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/fahrten/:id/zuweisen"
            element={
              <ProtectedRoute>
                <RideForm />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/fahrten/neu" 
            element={
              <ProtectedRoute>
                <RideForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten/dauerauftraege" 
            element={
              <ProtectedRoute>
                <RecurringOrdersList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/fahrten/dauerauftraege/:recurringOrderId/bearbeiten" 
            element={
              <ProtectedRoute>
                <RecurringOrderEditForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten" 
            element={
              <ProtectedRoute>
                <ShiftsList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/kalender" 
            element={
              <ProtectedRoute>
                <ShiftCalendar />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/:id" 
            element={
              <ProtectedRoute>
                <ShiftDetails />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <ShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichten/neu" 
            element={
              <ProtectedRoute>
                <ShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner" 
            element={
              <ProtectedRoute>
                <ShiftPlanner />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner/neu" 
            element={
              <ProtectedRoute>
                <PlannedShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/schichtplaner/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <PlannedShiftForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/berichte" 
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vorlagen" 
            element={
              <ProtectedRoute>
                <TemplatesList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vorlagen/neu" 
            element={
              <ProtectedRoute>
                <TemplateForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/vorlagen/:id/bearbeiten" 
            element={
              <ProtectedRoute>
                <TemplateForm />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/einstellungen"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/profil" 
            element={
              <ProtectedRoute>
                <div>Benutzerprofil (noch nicht implementiert)</div>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/passwort-vergessen" 
            element={
              <div>Passwort vergessen (noch nicht implementiert)</div>
            } 
          />
          
          {/* Default Route */}
          <Route
            path="/"
            element={
              isAuthenticated
                ? <Navigate to="/dashboard" />
                : <LandingPage directLogin={!isDefaultApp} />
            }
          />
          
          {/* 404 Route */}
          <Route 
            path="*" 
            element={
              <div>Seite nicht gefunden</div>
            } 
          />
        </Routes>
        
        {/* Cookie-Banner für alle Benutzer anzeigen */}
        <CookieBanner />
      </AuthCheckWrapper>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;